<template>
  <div class="flex flex-col divide-y">
    <div class="flex flex-col gap-1 pb-3">
      <div v-if="invoice.dossier" class="flex flex-col gap-1">
        <div>
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('global.dossier') }}
          </div>
          <button
            class="text-left leading-tight font-medium text-primary-600 hover:underline"
            @click="
              $router.push({
                name: 'dossier_edit_route_base',
                params: {
                  id: invoice.dossier.id
                }
              })
            "
          >
            {{ invoice.dossier?.reference }}
          </button>
        </div>
        <div v-if="invoice.dossier && invoice.dossier.concernedPerson">
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('global.concerned_person') }}
          </div>
          <button
            class="text-left leading-tight font-medium text-primary-600 hover:underline"
            @click="
              $router.push({
                name: 'dossier_edit_route_base',
                params: {
                  id: invoice.dossier.id
                }
              })
            "
          >
            {{ invoice.dossier.concernedPerson.full_name }}
          </button>
        </div>
      </div>
      <div v-else class="flex flex-col gap-1">
        <div v-if="!lockActions">
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('global.dossier') }}
          </div>
          <BaseButton
            :title="$t('button.link_to_dossier')"
            size="extra-small"
            @click="() => (linkToDossierModalOpen = true)"
          />
        </div>
      </div>
      <template v-if="validated">
        <div>
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.reference') }}
          </div>
          <div class="leading-tight font-medium text-gray-900">
            {{ invoice.reference }}
          </div>
        </div>
        <template
          v-if="invoice.enaos_transaction_id || invoice.enaos_webshop_reference"
        >
          <div v-if="invoice.enaos_transaction_id">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.enaos_transaction_id') }}
            </div>
            <div class="leading-tight font-medium text-gray-900">
              {{ invoice.enaos_transaction_id }}
            </div>
          </div>
          <div v-if="invoice.enaos_webshop_reference">
            <div
              class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
            >
              {{ $t('attributes.enaos_webshop_reference') }}
            </div>
            <div class="leading-tight font-medium text-gray-900">
              {{ invoice.enaos_webshop_reference }}
            </div>
          </div>
        </template>
        <div>
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.date') }}
          </div>
          <div class="leading-tight font-medium text-gray-900">
            {{
              $h.formatDate(
                invoice.emission_date,
                'DD-MM-YYYY',
                $i18n.locale.substring(0, 2)
              )
            }}
          </div>
        </div>
        <div>
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.due_date') }}
          </div>
          <div class="leading-tight font-medium text-gray-900">
            {{
              $h.formatDate(
                invoice.due_date,
                'DD-MM-YYYY',
                $i18n.locale.substring(0, 2)
              )
            }}
          </div>
        </div>
        <div
          v-if="responsible"
          data-test="responsible"
          class="mt-1 border-t border-gray-200 pt-2"
        >
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.responsable') }}
          </div>
          <div class="leading-tight font-medium text-gray-900">
            {{ responsible.full_name }}
          </div>
        </div>
        <div v-if="invoice.commission_note" data-test="commission">
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.commission') }}
          </div>
          <RouterLink
            :to="{
              name: 'commission_note_show_route',
              params: { id: invoice.commission_note.id }
            }"
            class="leading-tight font-medium text-primary-600 hover:underline"
          >
            {{ invoice.commission_note.reference }}
          </RouterLink>
          <div
            v-if="invoice.commission_note.is_manual"
            class="text-xs text-gray-500 capitalize-first"
          >
            {{ $t('global.commission_added_manually') }}
          </div>
          <div
            v-if="invoice.commission_note.amount_edited"
            class="text-xs text-gray-500 capitalize-first"
          >
            {{ $t('global.commission_amount_edited') }}
          </div>
        </div>
        <div
          v-else-if="invoice.commission_excluded_visible"
          data-test="commission-excluded"
        >
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('global.commission_excluded') }}
          </div>
          <button
            v-if="canManageCommission"
            type="button"
            class="text-sm font-medium text-primary-600 hover:underline disabled:opacity-50"
            data-test="reinclude-commission"
            :disabled="reincluding"
            @click="reincludeCommission"
          >
            {{ $t('button.reinclude') }}
          </button>
        </div>
        <ChiftContainer
          v-if="
            [DocTypes.INVOICE, DocTypes.CREDIT_NOTE].includes(
              invoice.doc_type
            ) &&
            $functionality.module.has('chift') &&
            (invoice?.account_diary?.chift_journal ||
              invoice?.account_diary?.chift_invoicing_enabled)
          "
          size="extra-small"
        >
          <template
            v-if="
              invoice?.account_diary?.chift_journal &&
              invoice?.account_diary?.chift_invoicing_enabled
            "
          >
            <div class="flex items-center gap-2">
              <hr class="flex-1 border-gray-200" />
              <span
                class="text-xs font-medium tracking-wide text-gray-400 uppercase"
              >
                {{ $t('attributes.accounting') }}
              </span>
              <hr class="flex-1 border-gray-200" />
            </div>
          </template>
          <ChiftAccountingInvoiceComponent
            v-if="invoice?.account_diary?.chift_journal"
            :invoice="invoice"
            :with-container="false"
            :disabled="
              metadata?.chift_errors &&
              Array.isArray(metadata?.chift_errors) &&
              metadata?.chift_errors.length > 0
            "
            @success="refreshInvoice"
          />
          <InvoicingPaymentSyncBanner
            v-if="invoice?.account_diary?.chift_journal"
            :invoice="invoice"
            :with-container="false"
          />
          <template
            v-if="
              invoice?.account_diary?.chift_journal &&
              invoice?.account_diary?.chift_invoicing_enabled
            "
          >
            <div class="flex items-center gap-2">
              <hr class="flex-1 border-gray-200" />
              <span
                class="text-xs font-medium tracking-wide text-gray-400 uppercase"
              >
                {{ $t('global.billing') }}
              </span>
              <hr class="flex-1 border-gray-200" />
            </div>
          </template>
          <ChiftInvoicingAccountingInvoiceComponent
            v-if="invoice?.account_diary?.chift_invoicing_enabled"
            :invoice="invoice"
            @success="refreshInvoice"
          />
          <!-- A Billit-only journal has no accounting block to hang it on. -->
          <InvoicingPaymentSyncBanner
            v-if="
              invoice?.account_diary?.chift_invoicing_enabled &&
              !invoice?.account_diary?.chift_journal
            "
            :invoice="invoice"
            :with-container="false"
          />
        </ChiftContainer>
      </template>
    </div>
    <div v-if="!lockActions" class="py-3">
      <template v-if="invoice.invoiceable_normalized">
        <InvoiceableSelector
          v-model:invoiceable="invoice.invoiceable_normalized"
          v-model:invoiceable-id="invoice.invoiceable_id"
          v-model:invoiceable-type="invoice.invoiceable_type"
          :invoice="invoice"
          @invoiceable-changed="updateInvoice"
        />
        <div
          v-if="
            isCompany ||
            invoice.fiscal_category !==
              invoice?.invoiceable?.invoiceable_company?.find(
                (company) => (company.id = invoice.company_id)
              )?.fiscal_category
          "
        >
          <div
            class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
          >
            {{ $t('attributes.fiscal_category') }}
          </div>
          <div class="leading-tight font-medium text-gray-900">
            {{
              fiscalCategories.find(
                (fiscalCategory) =>
                  fiscalCategory.id ===
                  invoice?.invoiceable?.invoiceable_company?.find(
                    (company) => (company.id = invoice.company_id)
                  )?.fiscal_category
              )?.name
            }}
          </div>
        </div>
        <div
          v-if="
            $functionality.module.has('peppol') &&
            [DocTypes.INVOICE, DocTypes.CREDIT_NOTE].includes(
              invoice.doc_type
            ) &&
            (invoice.invoiceable_normalized?.is_company ?? false)
          "
          class="mt-2"
        >
          <PeppolContactReadyComponent
            :invoiceable="invoice.invoiceable_normalized"
            :company-id="invoice.company?.id"
            @refresh-invoice="refreshInvoice"
          />
        </div>
      </template>
      <button
        v-else
        class="flex w-full flex-col justify-center rounded-lg border-2 border-dashed py-6 font-bold uppercase"
        @click="
          $router.push({
            name: 'invoice_edit_route',
            query: { replace: true }
          })
        "
      >
        {{ $t('button.add_wiki_contact') }}
      </button>
    </div>
    <div v-if="!!invoice.note_intern" class="flex flex-col gap-1 py-3">
      <div>
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('attributes.internal_notes') }}
        </div>
        <div
          class="leading-tight font-medium whitespace-pre-line text-gray-900"
        >
          {{ invoice.note_intern }}
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1 py-3">
      <StatusInput
        v-if="!(invoice.reference || !isCompany)"
        v-model="invoice.fiscal_category"
        :label="`${$t('attributes.fiscal_category')} (${$t('enums.invoicing.docTypes.' + invoice.doc_type)})`"
        :options="fiscalCategories"
        :disabled="invoice.reference || !isCompany"
        @change="changeFiscalCategory"
      />
      <div v-else>
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{
            `${$t('attributes.fiscal_category')} (${$t('enums.invoicing.docTypes.' + invoice.doc_type)})`
          }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          {{
            fiscalCategories.find(
              (fiscalCategory) => fiscalCategory.id === invoice.fiscal_category
            )?.name
          }}
        </div>
      </div>
      <div>
        <div class="truncate leading-tight text-gray-500 capitalize-first">
          {{ $t('attributes.total_tvac') }}
        </div>
        <div class="text-2xl leading-tight font-bold text-gray-900">
          {{ $h.formatCurrency(invoice.total_tvac, invoice.currency) }}
        </div>
      </div>

      <div>
        <div class="truncate leading-tight text-gray-500 capitalize-first">
          {{ $t('attributes.outstanding_balance') }}
        </div>
        <div class="text-2xl leading-tight font-bold text-gray-900">
          {{ $h.formatCurrency(getRest, invoice.currency) }}
        </div>
      </div>
      <div>
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('attributes.total_htva') }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          {{ $h.formatCurrency(invoice.total_htva, invoice.currency) }}
        </div>
      </div>
      <div>
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('attributes.total_hors_debours') }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          {{ $h.formatCurrency(invoice.total_hors_debours, invoice.currency) }}
        </div>
      </div>
      <div v-if="invoice.total_paid > 0">
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('invoicing.received_payments') }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          <button @click="isPaymentListModalOpen = true">
            {{ $h.formatCurrency(invoice.total_paid, invoice.currency) }}
          </button>
        </div>
      </div>
      <div v-if="ecartPayments?.length > 0">
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('enums.invoicing.paymentMode.DIS') }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          <button @click="isPaymentListModalOpen = true">
            {{
              $h.formatCurrency(
                ecartPayments.reduce(
                  (acc, p) => acc + parseFloat(p.amount ?? 0),
                  0
                ),
                invoice.currency
              )
            }}
          </button>
        </div>
      </div>
      <div v-if="cautionCheck">
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('enums.invoicing.paymentMode.CAU') }}
        </div>
        <div
          class="cursor-pointer leading-tight font-medium text-gray-900"
          @click="isPaymentListModalOpen = true"
        >
          {{ $h.formatCurrency(cautionCheck, invoice.currency) }}
        </div>
      </div>
      <div v-if="invoice.total_awaiting_payments > 0">
        <div
          class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
        >
          {{ $t('invoicing.awaiting_payments') }}
        </div>
        <div class="leading-tight font-medium text-gray-900">
          <button @click="isPaymentListModalOpen = true">
            {{
              $h.formatCurrency(
                invoice.total_awaiting_payments,
                invoice.currency
              )
            }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="metadata?.deposit_settings?.with_deposit"
      class="cursor-pointer py-3"
      @click="isDepositModalOpen = true"
    >
      <div class="truncate leading-tight text-gray-500 capitalize-first">
        {{ $t('attributes.deposit') }}
      </div>
      <div class="leading-tight font-medium text-gray-900">
        {{ $h.formatCurrency(invoice.deposit, invoice.currency) }}
      </div>
    </div>
    <div
      v-if="onlineMembersOtherThanMe?.length"
      class="flex flex-col gap-1 py-2"
    >
      <div
        class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
      >
        {{ $t('global.currently_on_this_page') }}
      </div>
      <div class="flex-row flex-wrap -space-x-1">
        <template v-for="member in onlineMembersOtherThanMe" :key="member.id">
          <AvatarComponent
            :user_id="member.id"
            :size="8"
            :display-status="false"
            class="transform transition duration-200 hover:z-50 hover:scale-125"
          />
        </template>
      </div>
    </div>
    <div v-if="invoice.from_invoicing?.length" class="flex flex-col gap-1 py-3">
      <h4
        class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
      >
        {{ $t('invoicing.created_from') }}
      </h4>
      <template v-for="from in invoice.from_invoicing" :key="from.id">
        <button
          class="text-left leading-tight font-medium text-primary-600 hover:underline"
          @click="
            confirmLink({
              name: 'invoice_show_route',
              params: {
                invoicing_id: from.id
              }
            })
          "
        >
          {{ $t('enums.invoicing.docTypes.' + from.doc_type) }}
          {{ from.reference }}
        </button>
      </template>
    </div>
    <div v-if="invoice.to_invoicing?.length" class="flex flex-col gap-1 pt-3">
      <h4
        class="truncate text-xs leading-tight font-medium text-gray-500 capitalize-first"
      >
        {{ $t('invoicing.converted_into') }}
      </h4>
      <template v-for="to in invoice.to_invoicing" :key="to.id">
        <button
          class="text-left leading-tight font-medium text-primary-600 hover:underline"
          @click="
            confirmLink({
              name: 'invoice_show_route',
              params: {
                invoicing_id: to.id
              }
            })
          "
        >
          {{ $t('enums.invoicing.docTypes.' + to.doc_type) }}
          {{ to.reference }}
        </button>
      </template>
    </div>
    <ConfirmModal ref="invoicingShowInformationConfim" />
    <PaymentListModal v-model:is-modal-open="isPaymentListModalOpen" />
    <DepositModal
      v-if="isDepositModalOpen"
      v-model:is-modal-open="isDepositModalOpen"
      @close="refreshInvoice"
    />
    <SearchDossierModal
      v-model:opened="linkToDossierModalOpen"
      @selected-dossier="updateInvoiceDossier"
    />
    <CommissionWarningModal ref="commissionWarningModal" />
  </div>
  <ConfirmModal ref="confirmChangeFiscalCategory">
    <template #default>
      <div
        v-if="
          [
            FiscalCategory.CO_CONTRACTOR,
            FiscalCategory.EXEMPT,
            FiscalCategory.INTRA_EU
          ].includes(invoice.fiscal_category) &&
          (!oldFiscalCategory ||
            [FiscalCategory.TAXABLE, FiscalCategory.NON_TAXABLE].includes(
              oldFiscalCategory
            ))
        "
        class="-mt-3 flex gap-x-1 text-xs font-medium text-red-500"
      >
        <FontAwesomeIcon :icon="['fal', 'exclamation-circle']" class="mt-0.5" />
        {{ $t('invoicing.fiscal_category_sub_title_0_modal') }}
      </div>
      <div
        v-else-if="
          oldFiscalCategory &&
          [
            FiscalCategory.CO_CONTRACTOR,
            FiscalCategory.EXEMPT,
            FiscalCategory.INTRA_EU
          ].includes(oldFiscalCategory) &&
          [FiscalCategory.TAXABLE, FiscalCategory.NON_TAXABLE].includes(
            invoice.fiscal_category
          )
        "
      >
        {{ $t('invoicing.fiscal_category_sub_title_modal') }}
      </div>
      <div class="mt-3 divide-y rounded-lg border">
        <label
          v-for="choice in choices"
          :key="choice.value"
          class="flex cursor-pointer flex-row gap-3 p-3"
          :class="{
            'text-red-500': choice.danger
          }"
        >
          <div class="flex shrink-0 items-center justify-center">
            <input
              v-model="selectedChoice"
              :value="choice.value"
              type="radio"
              name="delete_choice"
              class="text-primary focus:ring-0 focus:ring-offset-0 active:ring-0 active:ring-offset-0"
            />
          </div>
          <div class="flex-1">
            <h5 class="font-bold capitalize-first">{{ choice.title }}</h5>
            <p class="text-xs capitalize-first" v-html="choice.description"></p>
          </div>
        </label>
      </div>
    </template>
  </ConfirmModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import InvoiceableSelector from '@/views/invoicing/show/tabs/information/components/information/components/InvoiceableSelector.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import InvoicingPaymentSyncBanner from '@/views/invoicing/show/tabs/information/components/rightPanel/InvoicingPaymentSyncBanner.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import SearchDossierModal from '@ui/modal/dossier/SearchDossierModal.vue'
import AvatarComponent from '@c/AvatarComponent.vue'
import ChiftAccountingInvoiceComponent from '@/views/invoicing/show/tabs/information/components/information/components/ChiftAccountingInvoiceComponent.vue'
import ChiftInvoicingAccountingInvoiceComponent from '@/views/invoicing/show/tabs/information/components/information/components/ChiftInvoicingAccountingInvoiceComponent.vue'
import ChiftContainer from '@c/Partner/Chift/ChiftContainer.vue'
import DocTypes from '@/assets/enums/invoicing/DocTypes'
import PaymentMode from '@/assets/enums/invoicing/PaymentMode'
import PaymentListModal from '@ui/modal/payment/PaymentListModal.vue'
import StatusInput from '@c/addf-package/components/status/StatusInput.vue'
import FiscalCategory from '@/assets/enums/invoicing/FiscalCategory.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import PeppolContactReadyComponent from '@c/Partner/Peppol/PeppolContactReadyComponent.vue'
import CommissionWarningModal from '@c/baseComponents/modal/invoicing/CommissionWarningModal.vue'
import DepositModal from '@/views/invoicing/show/tabs/information/components/information/components/DepositModal.vue'

export default {
  name: 'InvoicingShowInformation',
  components: {
    CommissionWarningModal,
    DepositModal,
    PeppolContactReadyComponent,
    FontAwesomeIcon,
    StatusInput,
    PaymentListModal,
    ChiftAccountingInvoiceComponent,
    InvoicingPaymentSyncBanner,
    ChiftInvoicingAccountingInvoiceComponent,
    ChiftContainer,
    AvatarComponent,
    SearchDossierModal,
    BaseButton,
    ConfirmModal,
    InvoiceableSelector
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    lockActions: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      reincluding: false,
      linkToDossierModalOpen: false,
      linkToDossierId: null,
      isPaymentListModalOpen: false,
      isDepositModalOpen: false,
      oldFiscalCategory: null,
      selectedChoice: null
    }
  },
  computed: {
    ...mapGetters({
      fiscalCategories: 'metadata/getFiscalCategoriesMetadata',
      metadata: 'invoicing/getInvoiceMetadata',
      userById: 'metadata/getAllUserMetadata'
    }),
    // lifting an exclusion is a change of the commission notes: the same rights as on a note
    canManageCommission() {
      return (
        this.$can('manage_commission_notes') ||
        this.$can('manage_commission_settings')
      )
    },
    // same rule as the commission: the responsible of the document, else the one of its dossier
    responsible() {
      const id =
        this.invoice.invoice_responsable_id ||
        this.invoice.dossier?.responsable_id

      return id ? this.userById(id) : null
    },
    FiscalCategory() {
      return FiscalCategory
    },
    DocTypes() {
      return DocTypes
    },
    PaymentMode() {
      return PaymentMode
    },
    isCompany() {
      return (
        (this.invoice.invoiceable_type === 'App\\Models\\Person' &&
          this.invoice.invoiceable?.is_company) ||
        this.invoice.invoiceable_type === 'App\\Models\\Company'
      )
    },
    choices() {
      const currentFiscalCategory = this.fiscalCategories.find(
        (category) => category.id === this.oldFiscalCategory
      )
      return [
        {
          value: 'only_this_invoice',
          title: this.$t('invoicing.only_this_doc'),
          description: this.$t('invoicing.only_this_doc_desc', {
            fiscal_category: currentFiscalCategory?.name?.toUpperCase()
          }),
          danger: false
        },
        {
          value: 'for_all_invoices',
          title: this.$t('invoicing.for_all_invoices'),
          description: this.$t('invoicing.for_all_invoices_desc'),
          danger: true
        }
      ]
    },
    ...mapGetters({
      online_members: 'invoicing/getOnlineMembers',
      user: 'auth/getUser',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    onlineMembersOtherThanMe() {
      return this.online_members.filter(
        (online_member) => online_member.id !== this.user.id
      )
    },
    validated() {
      return this.invoice.reference && this.invoice.reference.length
    },
    checkDeduction() {
      return this.initialMetadata?.settings?.default_cheque_deduction
    },
    cautionCheck() {
      if (!this.invoice?.payments && !Array.isArray(this.invoice.payments)) {
        return null
      }

      const caution_checks = this.invoice.payments.filter(
        (payment) =>
          payment.payment_status === 'REC' &&
          payment.payment_mode === 'CAU' &&
          payment.caution_check_status === 'PEN'
      )

      if (caution_checks.length < 0) {
        return null
      }
      return caution_checks.reduce(
        (accumulator, currentValue) =>
          (accumulator += parseFloat(currentValue.amount)),
        0
      )
    },
    getTotalTvac() {
      return this.invoice.total_tvac ? parseFloat(this.invoice.total_tvac) : 0
    },
    getTotalPaid() {
      return this.invoice.total_paid ? parseFloat(this.invoice.total_paid) : 0
    },
    getRest() {
      return this.getTotalTvac - this.getTotalPaid
    },
    ecartPayments() {
      return this.invoice.payments.filter(
        (p) => p.payment_mode === PaymentMode.DISCREPENCY
      )
    }
  },
  mounted() {
    this.oldFiscalCategory = this.invoice.fiscal_category
  },
  methods: {
    ...mapActions({
      processUpdateInvoiceAction: 'invoicing/updateInvoice',
      processFetchInvoiceIDAction: 'invoicing/fetchInvoiceID',
      processReincludeCommissionAction:
        'commissionNote/reincludeCommissionDocument',
      processChangeInvoiceFiscalCategoryAction:
        'invoicing/changeInvoiceFiscalCategory'
    }),
    // lifts the exclusion: the commission of the sale can be counted again, the invoice is reloaded
    reincludeCommission() {
      this.reincluding = true
      this.processReincludeCommissionAction({ invoiceId: this.invoice.id })
        .then(() => this.processFetchInvoiceIDAction({ id: this.invoice.id }))
        .catch(() => {})
        .finally(() => {
          this.reincluding = false
        })
    },
    refreshInvoice() {
      this.processFetchInvoiceIDAction({ id: this.invoice.id })
    },
    updateInvoice() {
      this.processUpdateInvoiceAction({
        id: this.invoice.id,
        invoice: this.invoice
      }).then(async (response) => {
        this.$h.toastSuccess(this.$t('attributes.updated'))
        // Changing the responsible can move the commission of the sale
        if (response.commission_warning) {
          await this.$refs.commissionWarningModal.show(
            response.commission_warning
          )
        }
      })
    },
    updateInvoiceDossier(dossier_id) {
      this.linkToDossierModalOpen = false
      this.invoice.dossier_id = dossier_id
      this.updateInvoice()
    },
    async confirmLink(link) {
      let confirm = await this.$refs.invoicingShowInformationConfim.show({
        title: this.$t('invoicing.confirm_leave_invoice'),
        okButton: this.$t('global.yes')
      })
      if (confirm) {
        this.$router.push(link)
      }
    },
    changeFiscalCategory: async function () {
      let result = await this.$refs['confirmChangeFiscalCategory'].show({
        title: this.$t('invoicing.fiscal_category_title_modal')
      })
      if (result) {
        this.processChangeInvoiceFiscalCategoryAction({
          invoice_id: this.invoice.id,
          form_data: {
            fiscal_category: this.invoice.fiscal_category,
            old_fiscal_category: this.oldFiscalCategory,
            choice: this.selectedChoice ?? 'only_this_invoice'
          }
        }).then((response) => {
          this.$h.toastSuccess(this.$t('toasts.update_fiscal_category'))
          this.selectedChoice = null
        })
        this.oldFiscalCategory = this.invoice.fiscal_category
        return
      }
      this.invoice.fiscal_category = this.oldFiscalCategory
    }
  }
}
</script>
